import { useEffect, useState } from 'react'
import { User } from 'oidc-client-ts'
import { noop } from '../../framework/utils'
import { setValuesInternalAndWait } from '../../framework/actions'
import { TSG_ID_NONE, getTsgIdFromUrl, jwtDecodeCached } from '../../framework/tsg'
import { NotAuthenticated } from '../common/FullscreenError'
import LoadingPlaceholder from '../common/LoadingPlaceholder'
import { alertAndDisableTiming } from '../common/utils'
import { __info } from '../../init/debug'
import type { VFC, ReactElement } from 'react'
import type { SparkySettings } from '../../framework/microAppTypes'
import type { OidcState } from '../../framework/stateTypes'

const JWT_URL_HASH_KEY = 'tsg_access_token'

class BypassUserManager {
  user: User
  tsg_id: string | undefined
  events = {
    load() { },
    unload() { },
    addUserUnloaded() { },
    addUserLoaded() { },
    addSilentRenewError() { },
    removeUserUnloaded() { },
    removeUserLoaded() { },
    removeSilentRenewError() { },
  }
  _jwt_provided: boolean = true
  settings = { metadata: {} }
  constructor(jwt: string, decoded: any) {
    const {
      iss,
      sub,
      aud,
      exp,
      iat,
      auth_time,
      subname,
      scope,
      token_type,
      tsg_id,
    } = decoded
    this.user = new User({
      id_token: '',
      access_token: jwt,
      refresh_token: '',
      token_type,
      scope: Array.from(scope).join(' '),
      profile: {
        iss,
        sub,
        subname,
        aud,
        exp,
        iat,
        auth_time,
      },
      expires_at: exp,
      session_state: '',
      // state: undefined,
    })
    if (tsg_id) {
      this.tsg_id = tsg_id
      Object.assign(this.user, { tsg_id, _jwt_provided: true })
    }
  }
  async getUser() {
    if (!this.user?.expires_at || this.user.expires_at * 1000 < Date.now()) {
      return null
    }
    return this.user
  }
  async removeUser() {
  }
  async revokeTokens() {
  }
  async signinSilent(_args?: any) {
    return this.user!
  }
}

export const ProvidedJwtAuthProvider: VFC<{ children: ReactElement | null, settings: SparkySettings }> = ({ children }) => {
  const [isLoading, setLoading] = useState(true)
  const [user, setUser] = useState<User>()
  useEffect(() => {
    const setJwt = (jwt: string | null) => {
      const decodedJwt = jwt ? jwtDecodeCached(jwt) : undefined
      if (decodedJwt?.tsg_id) {
        if (decodedJwt.tsg_id !== TSG_ID_NONE) {
          const tsgId = getTsgIdFromUrl(window.location)
          if (tsgId && tsgId !== decodedJwt.tsg_id) {
            const errMsg = 'tsg_id in url is not matching the one in jwt token'
            console.error(errMsg, tsgId, decodedJwt.tsg_id)
            alertAndDisableTiming(errMsg)
            setLoading(false)
            // TODO: show error
            return
          }
        }
        const userMgr = new BypassUserManager(jwt!, decodedJwt)
        setValuesInternalAndWait({
          oidc: Object.defineProperties({
            oidcUser: userMgr.user,
          }, { // skip json.stringify
            login: {
              value: noop,
            },
            logout: {
              value: noop,
            },
            userManager: {
              value: userMgr,
            },
          }) as OidcState
        }).then(() => {
          setUser(userMgr.user)
          setLoading(false)
          __info(`bypass user oidc login by providing access token for tsg ${decodedJwt.tsg_id}`)
        })
      }
      else {
        setLoading(false)
      }
    }
    const { hash, pathname, search } = window.location
    if (hash) {
      const params = new URLSearchParams(hash.slice(1))
      const jwt = params.get(JWT_URL_HASH_KEY)
      if (jwt) {
        setJwt(jwt)
        window.history.replaceState(null, document.title, `${pathname}${search}`) // clear hash
        sessionStorage._provided_jwt = jwt
      }
    }
    else if (sessionStorage._provided_jwt) {
      setJwt(sessionStorage._provided_jwt)
    }
    else {
      setLoading(false)
    }
  }, [])

  if (isLoading) {
    return <LoadingPlaceholder />
  }

  if (!user) {
    return <NotAuthenticated />
  }

  return children
}

export default ProvidedJwtAuthProvider
